import {
	Box,
	Button,
	Inline,
	Icons,
	Select,
	Stack,
	useTimeout,
	Col,
	Grid,
	SROnly,
} from "@sembark-travel/ui-base"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useCallback, useMemo, useRef, useEffect } from "react"
import { Required } from "utility-types"
import * as Validator from "yup"
import { SelectTransportExtraServices, IExtraService } from "./../ExtraServices"
import {
	addUnit,
	getDiff,
	formatDate,
	dateToUTCString,
	startOf,
	isSame,
} from "@sembark-travel/datetime-utils"
import {
	Form,
	FieldArray,
	FormProps,
	TextInputField,
	SelectField,
	useFormState,
	FieldGroup,
	DatePickerField,
	validateFormValues,
	arrayMutators,
	EmptyNumberValidator,
	MoneyInputField,
} from "@sembark-travel/ui-form"
import { TTripDestination } from "../TripDestinations"
import { useCurrenciesCodesOfTenant } from "../Currencies"

function XHR(_: XHRInstance) {
	return {
		async getPrice<T>(transport_extras: T) {
			return Promise.resolve({ transport_extras })
			// return xhr
			//   .get("/prices", { params: { transport_extras } })
			//   .then(resp => resp.data)
		},
	}
}

export interface ExtraServicesParams {
	transport_extras?: Array<{
		service?: IExtraService
		currency: string
		price?: number
		date?: Date
		comments?: string
	}>
}

const validationSchema = Validator.object().shape({
	transport_extras: Validator.array().of(
		Validator.object().shape({
			service: Validator.object()
				.typeError("Service field is required")
				.required("Service field is required"),
			date: Validator.date().nullable(),
			price: EmptyNumberValidator().positive("Price should be positive"),
			comments: Validator.string(),
		})
	),
})

const validate = validateFormValues(validationSchema)

interface ExtraServicesFormProps {
	initialValues?: ExtraServicesParams
	onChange?: (
		data: Required<ExtraServicesParams, "transport_extras">["transport_extras"]
	) => void
	bookingFrom?: string
	bookingTo?: string
	shouldEmptyInitialValues?: boolean
	tripDestinations: Array<TTripDestination>
}
export function ExtraServicesForm({
	initialValues: initialValuesProp,
	shouldEmptyInitialValues = true,
	onChange,
	bookingFrom,
	bookingTo,
	tripDestinations,
}: ExtraServicesFormProps) {
	const currency = useMemo(
		() => tripDestinations.map((d) => d.currency).at(0) || "INR",
		[tripDestinations]
	)
	const allCurrencies = useCurrenciesCodesOfTenant()
	const xhr = useXHR()
	const INITIAL_VALUES: Required<ExtraServicesParams, "transport_extras"> =
		useRef({
			transport_extras: [
				{
					currency,
					service: undefined,
					date: undefined,
					price: undefined,
					comments: "",
				},
			],
		}).current

	const initialValues = useRef(
		initialValuesProp ||
			(shouldEmptyInitialValues ? { transport_extras: [] } : INITIAL_VALUES)
	).current
	const notifyOnChange = useCallback(
		(flattenValues: ExtraServicesParams) => {
			onChange &&
				onChange(
					flattenValues.transport_extras?.filter((te) => te.service) || []
				)
		},
		[onChange]
	)
	const bookingDates = useMemo(() => {
		const dates: Array<{ id: string; name: string; value: Date }> = []
		if (!bookingTo || !bookingFrom) return dates
		const days = getDiff(bookingTo, bookingFrom, "days")
		for (let i = 0; i <= days; i++) {
			const date = addUnit(bookingFrom, i, "day")
			dates.push({
				id: formatDate(date, "YYYY-MM-DD"),
				name: `${withOrdinalSuffix(i + 1)} Day (${formatDate(date, "ddd DD MMM")})`,
				value: startOf(date, "day"),
			})
		}
		return dates
	}, [bookingFrom, bookingTo])
	const onSubmit: FormProps<ExtraServicesParams>["onSubmit"] = useCallback(
		async (values) => {
			const transport_extras: Array<
				Omit<
					Required<typeof values>["transport_extras"][number],
					"date" | "service"
				> & {
					date: string
					service: string
				}
			> = []
			// flatten values so that we can show the prices for each row
			const flattenValues: Required<ExtraServicesParams, "transport_extras"> = {
				transport_extras: [],
			}
			values.transport_extras?.forEach((values) => {
				const { date, service, ...otherData } = values
				if (service) {
					flattenValues.transport_extras.push({
						...values,
					})
					transport_extras.push({
						...otherData,
						date: date ? dateToUTCString(startOf(date, "day")) : "",
						service: service && service.name,
					})
				}
			})
			return XHR(xhr)
				.getPrice(transport_extras)
				.then((data) => {
					flattenValues.transport_extras = flattenValues.transport_extras.map(
						(cab, i) => ({
							...cab,
							price: data.transport_extras[i].price,
						})
					)
					notifyOnChange(flattenValues)
				})
		},
		[xhr, notifyOnChange]
	)
	return (
		<Form<ExtraServicesParams>
			initialValues={initialValues}
			validate={validate}
			onSubmit={onSubmit}
			subscription={{}}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<FieldArray<
						Required<ExtraServicesParams>["transport_extras"][number]
					> name="transport_extras">
						{({ fields }) => (
							<Stack gap="4">
								{fields.map((name, index) => (
									<Box
										key={name}
										borderBottomWidth="4"
										borderColor="default"
										paddingBottom="4"
									>
										<Grid gap="4">
											<Col>
												<SelectField
													select={SelectTransportExtraServices}
													name={`${name}.service`}
													label="Service"
													placeholder="Select or add a service..."
													required
													creatable
													fetchOnMount
												/>
											</Col>
											<Col>
												<MoneyInputField
													label={`Total Price`}
													name={`${name}.price`}
													currencyFieldName={`${name}.currency`}
													currencies={allCurrencies}
												/>
											</Col>
											<Col>
												{bookingFrom && bookingTo ? (
													<FieldGroup<
														Required<ExtraServicesParams>["transport_extras"][number]["date"]
													>
														label="Day"
														name={`${name}.date`}
													>
														{({ input }) => {
															const { value } = input
															return (
																<Select
																	{...input}
																	options={bookingDates}
																	searchable={false}
																	placeholder="Select a date..."
																	required
																	value={
																		value
																			? bookingDates.find((d) =>
																					isSame(value, d.value, "day")
																				)
																			: undefined
																	}
																	onChange={(
																		bookingDay:
																			| (typeof bookingDates)[number]
																			| undefined
																	) => {
																		const day = bookingDay
																			? bookingDay.value
																			: undefined
																		input.onChange(day)
																	}}
																/>
															)
														}}
													</FieldGroup>
												) : (
													<DatePickerField
														label="Date"
														name={`${name}.date`}
														required
													/>
												)}
											</Col>
											<Col>
												<TextInputField
													name={`${name}.comments`}
													label="Comments"
													placeholder="Any comments regarding service"
												/>
											</Col>
											<Col paddingTop="6">
												<Inline gap="4">
													<Button
														onClick={() => fields.remove(index)}
														title="Remove"
														size="sm"
														level="tertiary"
													>
														<Icons.Cancel />
													</Button>
												</Inline>
											</Col>
										</Grid>
									</Box>
								))}
								<Box>
									<Button
										onClick={() =>
											fields.push(INITIAL_VALUES.transport_extras[0])
										}
									>
										<Icons.Plus /> Add Service
									</Button>
								</Box>
							</Stack>
						)}
					</FieldArray>
					<SubmitOnChange onChange={notifyOnChange} />
					<SROnly>
						<Button type="submit">Get Prices</Button>
					</SROnly>
				</form>
			)}
		</Form>
	)
}

function SubmitOnChange({
	onChange,
}: {
	onChange: (values: ExtraServicesParams) => void
}) {
	const { values } = useFormState<ExtraServicesParams>({
		subscription: { values: true },
	})
	const { set: setNotifyParentTimeout, clear: clearNotifyParentTimeout } =
		useTimeout()

	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange

	// notify parent
	useEffect(() => {
		setNotifyParentTimeout(() => {
			onChangeRef.current(values)
		}, 500)
		return () => clearNotifyParentTimeout()
	}, [values, setNotifyParentTimeout, clearNotifyParentTimeout])
	return null
}
